import { useEffect, useState } from "react";
import { ChartCard, Skeleton } from "@/components/reusable";
import { fetchDqFailureBreakdown } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { DqBucketDatum } from "@/types/analytics";

const SEVERITY_FILL: Record<DqBucketDatum["severity"], string> = {
  danger: "var(--color-error)",
  warn: "var(--color-warning)",
  info: "var(--color-info)",
  neutral: "var(--color-text-tertiary)",
  ok: "var(--color-brand-primary-dark)",
};

const SEVERITY_TEXT: Record<DqBucketDatum["severity"], string> = {
  danger: "text-[#791F1F]",
  warn: "text-[#854F0B]",
  info: "text-[var(--color-info)]",
  neutral: "text-[var(--color-text-tertiary)]",
  ok: "text-[var(--color-brand-primary-dark)]",
};

/**
 * Distribution of DQX failures across the product's bucket taxonomy.
 * Bucket labels match `dqxSummaryMetrics.DQX_SUMMARY_CHECK_TYPE_ORDER`.
 */
export function DqFailureBreakdown() {
  const filters = useAnalyticsFilters();
  const [data, setData] = useState<DqBucketDatum[] | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    fetchDqFailureBreakdown(filters).then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  const total = data?.reduce((a, d) => a + d.count, 0) ?? 0;
  const max = data?.reduce((m, d) => Math.max(m, d.count), 0) ?? 0;

  return (
    <ChartCard
      title="DQ failure breakdown"
      subtitle={`By check type · ${total} violation${total === 1 ? "" : "s"}`}
      minHeight={320}
    >
      {!data ? (
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height="h-3" />
          ))}
        </div>
      ) : data.every((d) => d.count === 0) ? (
        <EmptyBuckets />
      ) : (
        <ul className="flex flex-col h-full overflow-y-auto pr-1">
          {data.map((d) => (
            <li
              key={d.bucket}
              className="flex items-center gap-3 py-2 border-b border-[var(--color-border-light)] last:border-b-0 flex-shrink-0"
            >
              <span className="flex-1 text-[12px] capitalize text-[var(--color-text-primary)]">
                {d.bucket}
              </span>
              <div className="w-[90px] h-[5px] bg-[var(--color-bg-tertiary)] rounded-[3px] overflow-hidden flex-shrink-0">
                <div
                  className="h-full rounded-[3px] transition-[width] duration-500"
                  style={{
                    width: max > 0 ? `${(d.count / max) * 100}%` : "0%",
                    background: SEVERITY_FILL[d.severity],
                  }}
                />
              </div>
              <span
                className={`text-[11px] font-semibold min-w-[28px] text-right font-mono ${SEVERITY_TEXT[d.severity]}`}
              >
                {d.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </ChartCard>
  );
}

function EmptyBuckets() {
  return (
    <div className="py-8 text-center text-[12px] text-[var(--color-text-tertiary)]">
      No DQ failures in the current selection.
    </div>
  );
}
